// This is the only place the confirm gate decides which rendering applies.
// `typeConfirmGatePendingOutcome` mirrors `/contest/pending-outcome-get`'s
// response shape once ISSUE-237 lands.

import type { typeContestTiebreaker } from "@sportsmet/contest-engine";

/**
 * ISSUE-1309. A pick'em entrant's line from `/contest/pending-outcome-get`
 * `pickemStandings`: the numbers that tied, plus the final-round guess.
 */
export type typeConfirmGatePickemStanding = {
  points: number;
  titles: number;
  wins: number;
  finalRoundGuess: number | null;
  finalRoundGuessDiff: number | null;
};

export type typeConfirmGateEntrant = {
  entryId: string;
  displayName: string;
  /** ISSUE-1309. Present on a pick'em contest only. */
  pickemStanding?: typeConfirmGatePickemStanding | null;
};

/**
 * ISSUE-868. The optional `outstandingRound` from `/contest/pending-outcome-get`,
 * present only when the contest still carries a round that is ungraded and has
 * games. Missing on the wire is normalized to `null` here so every consumer has
 * a single shape to test.
 */
export type typeConfirmGateOutstandingRound = {
  roundId: string;
  eventIds: readonly string[];
};

export type typeConfirmGatePendingOutcome = {
  tiebreaker: typeContestTiebreaker;
  tieExists: boolean;
  medalName: string;
  winners: readonly typeConfirmGateEntrant[];
  tiedCandidates: readonly typeConfirmGateEntrant[];
  commissionerNamedWinnerIds: readonly string[] | null;
  isSeasonLastContest: boolean;
  outstandingRound: typeConfirmGateOutstandingRound | null;
  /**
   * ISSUE-1309. True when the server answered pick'em standings. A pick'em
   * entry is never out, so the gate offers no revive, and the naming step
   * words the tie for pick'em. Absent reads as survivor.
   */
  isPickem?: boolean;
};

export type typeConfirmGateOutcomeShape =
  "singleWinner" | "splitMedal" | "noWinner";

export type typeConfirmGateView =
  | {
      step: "naming";
      candidates: readonly typeConfirmGateEntrant[];
      isPickem?: boolean;
    }
  | {
      step: "outcome";
      shape: typeConfirmGateOutcomeShape;
      winners: readonly typeConfirmGateEntrant[];
      medalName: string;
      isSeasonLastContest: boolean;
      // ISSUE-868. Only the outcome step carries this: the naming step has no
      // confirm button, so there is no choice there for the round to inform.
      outstandingRound: typeConfirmGateOutstandingRound | null;
    };

export function resolveConfirmGateView(
  outcome: typeConfirmGatePendingOutcome,
): typeConfirmGateView {
  if (
    outcome.tiebreaker === "commissionerPick" &&
    outcome.tieExists &&
    outcome.commissionerNamedWinnerIds === null
  ) {
    return {
      step: "naming",
      candidates: outcome.tiedCandidates,
      ...(outcome.isPickem === true ? { isPickem: true } : {}),
    };
  }

  const { commissionerNamedWinnerIds } = outcome;
  const effectiveWinners =
    outcome.tiebreaker === "commissionerPick" &&
    commissionerNamedWinnerIds !== null
      ? outcome.tiedCandidates.filter((candidate) =>
          commissionerNamedWinnerIds.includes(candidate.entryId),
        )
      : outcome.winners;

  if (outcome.tiebreaker === "noWinner" && outcome.tieExists) {
    return {
      step: "outcome",
      shape: "noWinner",
      winners: [],
      medalName: outcome.medalName,
      isSeasonLastContest: outcome.isSeasonLastContest,
      outstandingRound: outcome.outstandingRound,
    };
  }

  if (effectiveWinners.length === 0) {
    return {
      step: "outcome",
      shape: "noWinner",
      winners: [],
      medalName: outcome.medalName,
      isSeasonLastContest: outcome.isSeasonLastContest,
      outstandingRound: outcome.outstandingRound,
    };
  }

  if (effectiveWinners.length === 1) {
    return {
      step: "outcome",
      shape: "singleWinner",
      winners: effectiveWinners,
      medalName: outcome.medalName,
      isSeasonLastContest: outcome.isSeasonLastContest,
      outstandingRound: outcome.outstandingRound,
    };
  }

  return {
    step: "outcome",
    shape: "splitMedal",
    winners: effectiveWinners,
    medalName: outcome.medalName,
    isSeasonLastContest: outcome.isSeasonLastContest,
    outstandingRound: outcome.outstandingRound,
  };
}
